import { Button, Helper, HorizontalDivider, Icon, Typography } from '@smartgift/frontend-toolkit';
import last from 'lodash/last';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { regex } from '@hero/schemas';
import { useNotification, useAuthStore } from '@hero/store/hooks';

import * as S from './style';

const CODE_LENGTH = 6;

function Verification({ contactInfo, onBack, onResend, onSubmit }) {
  const { t } = useTranslation();
  const { showInfo } = useNotification();

  const [inputValues, setInputValues] = useState(Array(CODE_LENGTH).fill(''));
  const [isVerifiying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { resendVerificationTimer, updateResendVerificationTimer, loadResendVerificationTimer } =
    useAuthStore();

  const inputRefs = Array(CODE_LENGTH)
    .fill()
    .map(() => useRef());

  const onKeyPress = event => {
    if (!regex.isNumber.test(event.key)) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (resendVerificationTimer > 0) {
      const interval = setInterval(() => {
        updateResendVerificationTimer(resendVerificationTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [resendVerificationTimer, updateResendVerificationTimer]);

  const onChange = (event, index) => {
    const { value } = event.target;

    setInputValues(prev => {
      prev[index] = last(value) || '';
      return [...prev];
    });

    const nextInputRef = value === '' ? inputRefs[index - 1] : inputRefs[index + 1];

    if (nextInputRef) {
      nextInputRef.current.focus();
    }
  };

  const onPaste = event => {
    event.stopPropagation();
    event.preventDefault();

    const clipboardData = event.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('Text').trim();

    const isAllNumbers = /^\d+$/.test(pastedText);
    if (!isAllNumbers || pastedText.length !== CODE_LENGTH) return;

    setInputValues(pastedText.split(''));
  };

  const renderCodeInputs = () =>
    inputValues.map((_, index) => {
      const elements = [];
      if (index === 3) {
        elements.push(<S.CodeInputSeparator key={elements.length + 1}>-</S.CodeInputSeparator>);
      }
      elements.push(
        <S.CodeInput
          key={index.toString()}
          ref={inputRefs[index]}
          onKeyPress={onKeyPress}
          onChange={event => onChange(event, index)}
          onPaste={event => onPaste(event)}
          value={inputValues[index]}
          data-testid={`code-input-${index}`}
          type="number"
        />
      );
      return elements;
    });

  const handleSendCode = async code => {
    setHasError(false);
    setIsVerifying(true);
    try {
      if (onSubmit) {
        await onSubmit({ code });
      }
    } catch (error) {
      setHasError(true);
    }
    setIsVerifying(false);
  };

  useEffect(() => {
    inputRefs[0]?.current?.focus();
  }, []);

  function handleSubmit() {
    const code = inputValues.join('');

    handleSendCode(code).finally(() => {
      if (inputRefs[0].current) {
        setInputValues(Array(CODE_LENGTH).fill(''));
        inputRefs[0].current.focus();
      }
    });
  }

  useEffect(() => {
    if (inputValues.every(item => item !== '')) {
      handleSubmit();
    }
  }, [inputValues]);

  return (
    <S.Verification>
      <Typography variant="subtitle3Bold">{t('verifyYourEmail')}</Typography>
      <Button
        variant="link"
        onClick={onBack}
        startIcon={<Icon name="left-chevron" />}
        style={{ margin: '24px 0' }}
        color="fg"
      >
        {t('back')}
      </Button>

      <S.VerificationHeader>
        <Icon name="login" style={{ marginRight: 4 }} size="lg" />
        <Typography variant="body2" emphasis="weak">
          {t('sentSixDigitCodeTo')}:<Typography>{!!contactInfo && contactInfo}</Typography>
        </Typography>
      </S.VerificationHeader>

      <HorizontalDivider $margin="16px 0" />

      <S.VerificationBody>
        <Typography
          variant="body2"
          style={{ marginBottom: 8, textAlign: 'center' }}
          emphasis="weak"
        >
          {t('codeExpiresShortlyEnterItSoon')}
        </Typography>
        <S.CodeInputs>{renderCodeInputs()}</S.CodeInputs>

        {hasError && <Helper text={t('invalidValidationCode')} error={hasError} />}
      </S.VerificationBody>

      <S.VerificationFooter>
        <Typography variant="body2" emphasis="weak">
          {t('didntGetTheCode')}
        </Typography>
        <Button
          variant="link"
          onClick={async () => {
            setIsResending(true);
            try {
              if (onResend) {
                await onResend();
                showInfo({
                  message: t('yourCodeHasBeenSent', { contactInfo }),
                  type: 'toast'
                });
              }
              loadResendVerificationTimer();
            } catch (_error) {
              showInfo({
                message: t('errorSendingCode'),
                type: 'toast'
              });
            }
            setIsResending(false);
          }}
          disabled={resendVerificationTimer > 0 || isResending}
          color="neutral"
        >
          {t('resendCode')}
          {resendVerificationTimer > 0 && (
            <S.VerificationResend>
              {Math.floor(resendVerificationTimer / 60)}:
              {resendVerificationTimer % 60 < 10
                ? `0${resendVerificationTimer % 60}`
                : resendVerificationTimer % 60}
            </S.VerificationResend>
          )}
        </Button>
      </S.VerificationFooter>
      <Button
        block
        style={{ margin: '16px 0' }}
        onClick={handleSubmit}
        disabled={inputValues.some(v => !v)}
        loading={isVerifiying}
      >
        {t('verify')}
      </Button>
      <Typography variant="body2" emphasis="weak" style={{ fontWeight: 600 }} as="div">
        {t('cantFindYourCode')}{' '}
        <Typography variant="body2" style={{ display: 'inline-block', fontWeight: 400 }}>
          {t('checkYourSpamFolder')}
        </Typography>
      </Typography>
    </S.Verification>
  );
}

export { Verification };
